import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSavedWork } from '@/offline/useSavedWork';
import { useEffect, useState } from 'react';
import { api, UNREACHABLE_MESSAGE } from '@/api/client';
import { ActionBar, DataGrid, type DesktopAction, type GridColumn } from '@/components/desktop';
import { AppIcon } from '@/components/AppIcon';
import { StorePicker } from '@/components/StorePicker';
import { ComposePanel } from '@/components/ComposePanel';
import { DocumentFrame } from '@/components/DocumentFrame';
import { SavedWorkDetail } from '@/components/SavedWorkDetail';
import { DeleteButton, HistoryPanel, ReverseButton, ReviewSummaryDialog, printDocument } from '@/components/documents';
import { DraftNotice } from '@/components/DraftNotice';
import { DrawLines, emptyDrawLine, type DrawLine } from '@/components/DrawLines';
import { ENTRY_ROWS, padRows } from '@/components/lineRows';
import { useDraft } from '@/stores/drafts';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  OkNote,
  Pager,
  Select,
  StatusPill,
  Tile,
  errorMessage,
  money,
  quantity,
  rate,
  shortDate,
  todayIso,
} from '@/components/ui';
import { holdingReadKey, useSkuCatalog, useStockInvalidation, useStores } from '@/hooks/inventory';
import { fetchOrCached, isOfflineGap, NOT_ON_THIS_PC, NotOnThisPcError } from '@/offline/availability';
import { LoadingNote, OfflineDetails } from '@/components/OfflineDetails';
import { queuedOut } from '@/offline/projection';
import { announceSavedOffline } from '@/stores/savedOffline';
import { quantityLeft } from '@/hooks/stockSend';
import { awaitWork, retryWork, saveWork, savedWork, takeBack } from '@/offline/engine';
import { confirmAction } from '@/stores/confirm';
import type { NewOperation, Operation } from '@/offline/outbox';
import { piecesOf } from '@/offline/pieces';
import { useConnection } from '@/stores/connection';
import { useLiveFlocks } from '@/hooks/sales';
import { useFarmScopedStore } from '@/hooks/useFarmScopedStore';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import type { ScreenIntent } from '@/stores/shellNav';
import { useScreenIntent } from '@/stores/shellNav';
import { packedSuffix } from '@/packing';

const PAGE_SIZE = 50;

interface ConsumptionDetail {
  id: string;
  documentNo: string;
  status: string;
  storeId: string;
  store: string;
  storeCode: string;
  skuId: string;
  skuCode: string;
  skuName: string;
  uom: string;
  batchId: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  shedId: string | null;
  shed: string | null;
  flockId: string | null;
  flock: string | null;
  qty: string;
  unitCost: string;
  value: string;
  consumedOn: string;
  issuedTo: string | null;
  remarks: string | null;
  version: number;
  postedAt: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
}

interface ConsumptionRow {
  id: string;
  documentNo: string;
  status: string;
  consumedOn: string;
  store: string;
  storeCode: string;
  skuCode: string;
  skuName: string;
  uom: string;
  uomPrecision: number;
  qty: string;
  value?: string;
  batchNo: string | null;
  shed: string | null;
  flock: string | null;
  issuedTo: string | null;
}

type ComposeState =
  | { mode: 'create'; intent?: ScreenIntent | undefined; seed?: ConsumptionDetail | undefined }
  | { mode: 'edit'; detail: ConsumptionDetail }
  /** Work the server refused, loaded back to be put right (.docs/023). */
  | { mode: 'fix'; op: Operation };

/** An issue's own screen: what left which store, where it went, and what has happened to it since. */
function ConsumptionDocument({ detail, showCost }: { detail: ConsumptionDetail; showCost: boolean }) {
  return (
    <>
      {detail.reversedAt && (
        <p className="docpage__note docpage__note--alert" role="note">
          Reversed on {shortDate(detail.reversedAt)}. Reason: {detail.reversalReason ?? '—'}
        </p>
      )}
      {detail.status === 'DRAFT' && (
        <p className="docpage__note" role="note">
          This is a draft. Nothing has left stock yet.
        </p>
      )}

      <div className="tiles">
        <Tile label="Quantity" value={`${quantity(detail.qty, 2)} ${detail.uom}`} />
        {showCost && <Tile label="Value" value={money(detail.value)} />}
        <Tile label="Lot" value={detail.batchNo ?? '—'} />
        <Tile label="Store" value={detail.storeCode} />
      </div>

      <section className="stack docpage__section">
        <h3 className="formsection">Issue</h3>
        <dl className="detaillist">
          <dt>Item</dt>
          <dd>
            <span className="mono">{detail.skuCode}</span> {detail.skuName}
          </dd>
          <dt>From store</dt>
          <dd>
            {detail.storeCode} — {detail.store}
          </dd>
          <dt>Date</dt>
          <dd>{shortDate(detail.consumedOn)}</dd>
          <dt>Lot</dt>
          <dd>
            {detail.batchNo
              ? `${detail.batchNo}${detail.expiryDate ? ` · expires ${shortDate(detail.expiryDate)}` : ''}`
              : '—'}
          </dd>
          {showCost && (
            <>
              <dt>Unit cost</dt>
              <dd>{rate(detail.unitCost)}</dd>
            </>
          )}
          {detail.postedAt && (
            <>
              <dt>Posted on</dt>
              <dd>{shortDate(detail.postedAt)}</dd>
            </>
          )}
        </dl>
      </section>

      <section className="stack docpage__section">
        <h3 className="formsection">Where it went</h3>
        <dl className="detaillist">
          <dt>Destination</dt>
          <dd>
            {detail.shed ? (
              <span className="withicon">
                <AppIcon kind="shed" size={14} />
                {detail.shed}
              </span>
            ) : detail.flock ? (
              <span className="withicon">
                <AppIcon kind="flock" size={14} />
                {detail.flock}
              </span>
            ) : (
              '—'
            )}
          </dd>
          <dt>Issued to</dt>
          <dd>{detail.issuedTo ?? '—'}</dd>
          <dt>Remarks</dt>
          <dd>{detail.remarks ?? '—'}</dd>
        </dl>
      </section>

      <section className="stack docpage__section">
        <h3 className="formsection">History</h3>
        <HistoryPanel path={`/api/v1/inventory/consumption/${detail.id}/history`} />
      </section>
    </>
  );
}

/** Quantities are held to four decimals; splitting in whole ten-thousandths
 *  keeps the portions summing exactly to what was typed. */
const QTY_SCALE = 10_000;

interface LotOption {
  id: string;
  batchNo: string;
  expiryDate: string | null;
  receivedAt: string;
  qty: string;
}

/** One issue document a line turns into: the lot it draws on (blank for an
 *  item not tracked by lot) and how much comes out of it. */
const CONSUMPTION = '/api/v1/inventory/consumption';

/**
 * What a queued Stock OUT carries besides its request: the form as it was
 * typed, one item and one lot. It is what the list shows for work not yet
 * sent, and what Fix it loads back (.docs/023).
 */
export interface QueuedIssueForm {
  header: {
    storeId: string;
    consumedOn: string;
    shedId: string;
    flockId: string;
    issuedTo: string;
    remarks: string;
  };
  line: DrawLine;
}

/** What a waiting or refused row is made of. */
export type QueuedIssue = Operation;

const LATER = 'Saved on this PC. It will be done when the connection is back.';

/**
 * Saves one action on a document through the outbox (.docs/023): answered
 * now with a line, kept on this PC without one. A refusal while the screen is
 * open is taken back out and thrown, for the screen to show.
 */
async function saveOne(op: NewOperation): Promise<'sent' | 'waiting'> {
  const saved = await saveWork([op]);
  // Kept for later: said so where it cannot be missed.
  if (saved.outcome === 'waiting') void announceSavedOffline({ what: op.describes, closeLabel: 'OK' });
  if (saved.outcome === 'sent' || saved.outcome === 'waiting') return saved.outcome;
  for (const o of saved.ops) if (o.state !== 'sent') await takeBack(o.id).catch(() => undefined);
  throw new Error(saved.ops.find((o) => o.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE);
}

/** A Stock OUT saved on this PC, shown in the list until the server has it. */
function queuedRow(op: Operation, stores: ReadonlyArray<{ id: string; code: string; nameEn: string }>): ConsumptionRow {
  const form = op.form as QueuedIssueForm;
  const [code = '', ...name] = form.line.skuLabel.split(' — ');
  const store = stores.find((s) => s.id === form.header.storeId);
  return {
    id: `op:${op.id}`,
    documentNo: 'Not sent yet',
    status: op.state === 'refused' ? 'REFUSED' : 'NOT_SENT',
    consumedOn: form.header.consumedOn,
    skuCode: code,
    skuName: name.join(' — '),
    qty: form.line.qty,
    uom: '',
    uomPrecision: 2,
    batchNo: null,
    storeCode: store?.code ?? '',
    store: store?.nameEn ?? '',
    shed: null,
    flock: null,
    issuedTo: form.header.issuedTo || null,
    value: null,
  } as unknown as ConsumptionRow;
}

interface IssuePortion {
  batchId: string;
  batchNo: string | null;
  expiryDate: string | null;
  qty: string;
  /** True when the lot was picked for the user rather than chosen. */
  auto: boolean;
}

/**
 * Splits an issue of a lot-tracked item across the lots the store holds,
 * oldest first (FIFO), so the user need not pick one. Refuses when the lots
 * together do not hold enough, rather than issuing part of it.
 */
function splitAcrossLots(lots: readonly LotOption[], qty: string, itemCode: string): IssuePortion[] {
  let remaining = Math.round(Number(qty) * QTY_SCALE);
  const portions: IssuePortion[] = [];
  for (const lot of lots) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, Math.round(Number(lot.qty) * QTY_SCALE));
    if (take <= 0) continue;
    portions.push({ batchId: lot.id, batchNo: lot.batchNo, expiryDate: lot.expiryDate, qty: (take / QTY_SCALE).toString(), auto: true });
    remaining -= take;
  }
  if (remaining > 0) {
    const held = lots.reduce((sum, l) => sum + Number(l.qty), 0);
    throw new Error(`${itemCode}: only ${held} held across all lots, ${qty} requested.`);
  }
  return portions;
}

function lotLabel(p: IssuePortion): string | null {
  if (!p.batchNo) return null;
  const expiry = p.expiryDate ? ` · exp ${shortDate(p.expiryDate)}` : '';
  return `${p.batchNo}${expiry}${p.auto ? ' (auto)' : ''}`;
}

export function ConsumptionLog() {
  const intent = useScreenIntent('inv-consume');
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const showCost = can('inv.price.read');
  const [compose, setCompose] = useState<ComposeState | null>(() =>
    intent?.action === 'create' && can('inv.consumption.create') ? { mode: 'create', intent } : null,
  );
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Sent here to open one issue: from Offline work, once it has gone.
  const [openKey, setOpenKey] = useState<string | null>(() => intent?.recordId ?? null);
  const [note, setNote] = useState<string | null>(null);
  const [repeatBusy, setRepeatBusy] = useState(false);
  const { data: stores } = useStores();

  // Work saved on this PC and not yet with the server leads the list
  // (.docs/023): what somebody is still waiting on, with no number yet.
  const outbox = useSavedWork();
  const queued = (outbox.data ?? [])
    .filter((op) => op.kind === 'consumption.create' && op.state !== 'sent' && op.form !== undefined)
    .reverse();
  const opFor = (row: ConsumptionRow) => queued.find((op) => `op:${op.id}` === row.id);

  // Sent here from Offline work to put refused work right.
  useEffect(() => {
    const opId = intent?.fixOperation;
    if (!opId) return;
    void savedWork().then((ops) => {
      const op = ops.find((o) => o.id === opId);
      if (op?.form) setCompose({ mode: 'fix', op });
    });
  }, [intent?.fixOperation]);

  async function throwAway(op: Operation) {
    const ok = await confirmAction({
      title: 'Throw this work away?',
      message: `"${op.describes}" will be removed from this PC. The server will never see it.`,
      confirmLabel: 'Throw it away',
      cancelLabel: 'Keep it',
      danger: true,
    });
    if (!ok) return;
    await takeBack(op.id);
    setSelectedKey(null);
    setNote('Thrown away.');
  }

  const list = useQuery({
    queryKey: ['inv', 'consumption', farmIds, skip],
    queryFn: () => api.get('/api/v1/inventory/consumption', { take: PAGE_SIZE, skip, ...farmScopeQuery(farmIds) }),
  });
  const { data, isPending, error } = list;

  const detailQuery = useQuery({
    queryKey: ['inv', 'consumption', 'detail', openKey],
    queryFn: async () =>
      (await api.get(`/api/v1/inventory/consumption/${openKey}` as '/api/v1/inventory/consumption/{id}')) as unknown as ConsumptionDetail,
    // A row saved on this PC has no issue on the server yet.
    enabled: openKey !== null && !openKey.startsWith('op:'),
  });
  const { data: detail, error: detailError, isFetching: detailFetching } = detailQuery;

  // Each goes through the outbox, carrying the version that was seen where the
  // server takes one, so a document changed since is refused rather than acted
  // on unseen (.docs/023).
  const post = useMutation({
    mutationFn: (doc: { id: string; documentNo: string; version?: number | undefined }) =>
      saveOne({
        kind: 'consumption.post',
        method: 'POST',
        path: `${CONSUMPTION}/${doc.id}/post`,
        ...(doc.version ? { body: { version: doc.version } } : {}),
        describes: `Posting ${doc.documentNo}`,
        screen: 'inv-consume',
      }),
    onSuccess: (outcome) => { invalidate(); setNote(outcome === 'sent' ? 'Posted to the ledger.' : LATER); },
  });
  const del = useMutation({
    mutationFn: ({ id, version, documentNo }: { id: string; version: number; documentNo: string }) =>
      saveOne({
        kind: 'consumption.cancel',
        method: 'DELETE',
        path: `${CONSUMPTION}/${id}?version=${version}`,
        describes: `Deleting draft ${documentNo}`,
        screen: 'inv-consume',
      }),
    onSuccess: (outcome) => { invalidate(); setOpenKey(null); setSelectedKey(null); setNote(outcome === 'sent' ? 'Draft deleted.' : LATER); },
  });
  const reverse = useMutation({
    mutationFn: ({ id, reason, documentNo }: { id: string; reason: string; documentNo: string }) =>
      saveOne({
        kind: 'consumption.reverse',
        method: 'POST',
        path: `${CONSUMPTION}/${id}/reverse`,
        body: { reason },
        describes: `Reversing ${documentNo}`,
        screen: 'inv-consume',
      }),
    onSuccess: (outcome) => { invalidate(); setNote(outcome === 'sent' ? 'Reversed.' : LATER); },
  });

  const rows = skip === 0 ? [...queued.map((op) => queuedRow(op, stores ?? [])), ...(data?.items ?? [])] : (data?.items ?? []);

  /** Moving between the list and an issue: what was said on one is not news on the other. */
  function show(key: string | null) {
    setOpenKey(key);
    setNote(null);
    post.reset();
    del.reset();
    reverse.reset();
  }

  /** The whole document: from the server, or as this PC last loaded it. */
  async function fetchFull(id: string): Promise<ConsumptionDetail> {
    return fetchOrCached(
      ['inv', 'consumption', 'detail', id],
      async () => (await api.get(`/api/v1/inventory/consumption/${id}` as '/api/v1/inventory/consumption/{id}')) as unknown as ConsumptionDetail,
      'This document',
    );
  }

  async function edit(row: ConsumptionRow) {
    try {
      setCompose({ mode: 'edit', detail: await fetchFull(row.id) });
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that issue to edit it'));
    }
  }

  async function repeat(row: ConsumptionRow) {
    setRepeatBusy(true);
    try {
      setCompose({ mode: 'create', seed: await fetchFull(row.id) });
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that issue to repeat it'));
    } finally {
      setRepeatBusy(false);
    }
  }

  async function print(row: ConsumptionRow) {
    try {
      printIssue(await fetchFull(row.id));
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that issue to print it'));
    }
  }

  function printIssue(full: ConsumptionDetail) {
    printDocument({
      title: `Issue ${full.documentNo}`,
      facts: [
        ['Status', full.status],
        ['Item', `${full.skuCode} — ${full.skuName}`],
        ['Store', `${full.storeCode} — ${full.store}`],
        ['Quantity', `${quantity(full.qty, 2)} ${full.uom}`],
        ['Date', shortDate(full.consumedOn)],
        ['Lot', full.batchNo ?? '—'],
        ['Destination', full.shed ?? full.flock ?? '—'],
        ['Issued to', full.issuedTo ?? '—'],
        ...(showCost ? ([['Value', money(full.value)]] as Array<[string, string]>) : []),
      ],
      showCost,
    });
  }

  function rowActions(row: ConsumptionRow): DesktopAction[] {
    const op = opFor(row);
    if (op) {
      return [
        { label: 'Open', icon: 'details' as const, shortcut: 'Enter', onSelect: () => show(row.id), allowed: true },
        {
          label: 'Fix it', icon: 'edit' as const,
          onSelect: () => setCompose({ mode: 'fix', op }),
          allowed: can('inv.consumption.create'),
          enabled: op.state === 'refused',
          reason: 'Only work the server refused needs fixing',
        },
        {
          label: 'Throw it away', icon: 'remove' as const,
          onSelect: () => void throwAway(op),
          allowed: true,
          enabled: op.state === 'refused',
          reason: 'Work still waiting to be sent cannot be thrown away',
        },
      ];
    }
    return [
      { label: 'Open', icon: 'details' as const, shortcut: 'Enter', onSelect: () => show(row.id), allowed: true },
      {
        label: 'Edit', icon: 'edit' as const,
        onSelect: () => void edit(row),
        allowed: can('inv.consumption.update'),
        enabled: row.status === 'DRAFT',
        reason: 'Only a draft can be edited',
      },
      {
        label: 'Post', icon: 'post' as const,
        onSelect: () => post.mutate({ id: row.id, documentNo: row.documentNo, version: detail?.id === row.id ? detail.version : undefined }),
        allowed: can('inv.consumption.post'),
        enabled: row.status === 'DRAFT',
        reason: 'Only a draft can be posted',
      },
      { label: 'Repeat', icon: 'repeat' as const, onSelect: () => void repeat(row), allowed: can('inv.consumption.create'), enabled: !repeatBusy, separatorBefore: true },
      { label: 'Print', icon: 'print' as const, onSelect: () => void print(row), allowed: true },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    { label: 'Issue stock', icon: 'outbound', primary: true, onSelect: () => setCompose({ mode: 'create' }), allowed: can('inv.consumption.create') },
  ];

  const columns: Array<GridColumn<ConsumptionRow>> = [
    { key: 'doc', header: 'Document', width: 150, render: (r) => <span className="withicon"><AppIcon kind="outbound" size={14} /><span className="mono">{r.documentNo}</span></span> },
    { key: 'date', header: 'Date', width: 100, render: (r) => shortDate(r.consumedOn) },
    { key: 'item', header: 'Item', width: 220, render: (r) => (<><span className="mono">{r.skuCode}</span> {r.skuName}</>) },
    { key: 'qty', header: 'Quantity', align: 'end', width: 110, render: (r) => (<>{quantity(r.qty, r.uomPrecision)} <span className="dim">{r.uom}</span></>) },
    { key: 'lot', header: 'Lot', width: 120, render: (r) => <span className="dim mono">{r.batchNo ?? '—'}</span> },
    { key: 'store', header: 'Store', width: 100, render: (r) => <span className="dim">{r.storeCode}</span> },
    { key: 'dest', header: 'Destination', width: 140, render: (r) => (r.shed ? <span className="dim withicon"><AppIcon kind="shed" size={14} />{r.shed}</span> : r.flock ? <span className="dim withicon"><AppIcon kind="flock" size={14} />{r.flock}</span> : <span className="dim">—</span>) },
    { key: 'issuedTo', header: 'Issued to', width: 140, render: (r) => <span className="dim">{r.issuedTo ?? '—'}</span> },
    ...(showCost ? [{ key: 'value', header: 'Value', align: 'end' as const, width: 120, render: (r: ConsumptionRow) => money(r.value) }] : []),
    { key: 'status', header: 'Status', width: 110, render: (r) => <StatusPill status={r.status} /> },
  ];

  if (compose) {
    return (
      <ConsumptionCompose
        state={compose}
        onCancel={() => setCompose(null)}
        onSaved={(message) => { setCompose(null); setNote(message); }}
      />
    );
  }

  // Work saved on this PC: shown as it was typed until the server has it.
  const savedPiece = openKey?.startsWith('op:')
    ? piecesOf(outbox.data ?? []).find((p) => p.ops.some((op) => `op:${op.id}` === openKey))
    : undefined;
  if (savedPiece) {
    return (
      <SavedWorkDetail
        piece={savedPiece}
        all={outbox.data ?? []}
        backLabel="Back to Stock OUT"
        onBack={() => show(null)}
        onFix={(op) => { show(null); setCompose({ mode: 'fix', op }); }}
        onOpenDocument={(id) => show(id)}
        onDone={(said) => { show(null); setNote(said); }}
      />
    );
  }

  if (openKey && !openKey.startsWith('op:')) {
    const openRow = rows.find((r) => r.id === openKey);
    const canEdit = detail?.status === 'DRAFT' && can('inv.consumption.update');
    const canDelete = detail?.status === 'DRAFT' && can('inv.consumption.delete');
    const canPost = detail?.status === 'DRAFT' && can('inv.consumption.post');
    const canReverse = detail?.status === 'POSTED' && can('inv.consumption.reverse');
    return (
      <DocumentFrame
        onBack={() => show(null)}
        backLabel="Back to Stock OUT"
        title={
          detail && (
            <>
              <h2 className="withicon">
                <AppIcon kind="outbound" size={18} />
                <span className="mono">{detail.documentNo}</span>
              </h2>
              <StatusPill status={detail.status} />
              <span className="muted">{shortDate(detail.consumedOn)}</span>
            </>
          )
        }
        actions={
          detail && (
            <>
              <button type="button" className="btn btn--withicon" onClick={() => printIssue(detail)}>
                <AppIcon kind="print" size={15} />
                Print
              </button>
              {can('inv.consumption.create') && (
                <button
                  type="button"
                  className="btn btn--withicon"
                  onClick={() => {
                    // Leaves this issue behind: "Back to list" means the list.
                    show(null);
                    setCompose({ mode: 'create', seed: detail });
                  }}
                >
                  <AppIcon kind="repeat" size={15} />
                  Repeat
                </button>
              )}
              {canDelete && (
                <DeleteButton pending={del.isPending} onDelete={() => del.mutate({ id: detail.id, version: detail.version, documentNo: detail.documentNo })} />
              )}
              {canReverse && (
                <ReverseButton pending={reverse.isPending} onReverse={(reason) => reverse.mutate({ id: detail.id, reason, documentNo: detail.documentNo })} />
              )}
              {canEdit && (
                <button type="button" className="btn btn--withicon" onClick={() => setCompose({ mode: 'edit', detail })}>
                  <AppIcon kind="edit" size={15} />
                  Edit
                </button>
              )}
              {canPost && (
                <button
                  type="button"
                  className="btn btn--primary btn--withicon"
                  disabled={post.isPending || detailFetching}
                  onClick={() => post.mutate({ id: detail.id, documentNo: detail.documentNo, version: detail.version })}
                >
                  <AppIcon kind="post" size={15} />
                  {post.isPending ? 'Posting…' : 'Post'}
                </button>
              )}
            </>
          )
        }
      >
        {note && <OkNote message={note} />}
        {(post.error ?? del.error ?? reverse.error) && (
          <ErrorNote message={errorMessage(post.error ?? del.error ?? reverse.error, 'Action failed')} />
        )}
        {detailError && !detail && <ErrorNote message={detailError.message} />}
        {!detail && isOfflineGap(detailQuery) && openRow && <OfflineDetails row={openRow} columns={columns} />}
        {!detail && !detailError && !(isOfflineGap(detailQuery) && openRow) && <LoadingNote what="Loading the issue…" />}
        {detail && <ConsumptionDocument detail={detail} showCost={showCost} />}
      </DocumentFrame>
    );
  }

  return (
    <div className="workbench">
      <div className="workbench__main">
        <ActionBar actions={toolbarActions} />

        {note && <OkNote message={note} />}
        {(post.error ?? del.error ?? reverse.error) && (
          <ErrorNote message={errorMessage(post.error ?? del.error ?? reverse.error, 'Action failed')} />
        )}
        {error && <ErrorNote message={error.message} />}

        <DataGrid
          id="documents-consumption"
          label="Consumption"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          // A row saved on this PC opens as what was saved, and where it has got to.
          onOpen={(row) => show(row.id)}
          openOnClick
          actions={rowActions}
          loading={isPending && !isOfflineGap(list)}
          empty={isOfflineGap(list) ? NOT_ON_THIS_PC : 'Nothing issued yet.'}
        />
        <Pager skip={skip} take={PAGE_SIZE} total={data?.total ?? 0} onChange={setSkip} />
      </div>
    </div>
  );
}

function ConsumptionCompose({
  state,
  onCancel,
  onSaved,
}: {
  state: ComposeState;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const { data: stores } = useStores();
  const catalog = useSkuCatalog();

  const initial = state.mode === 'edit' ? state.detail : undefined;
  const seed = initial ?? (state.mode === 'create' ? state.seed : undefined);
  const intent = state.mode === 'create' ? state.intent : undefined;
  const editing = initial !== undefined;
  // Refused work being put right: its form comes back as it was typed.
  const fixing = state.mode === 'fix' ? state.op : undefined;
  const fixForm = fixing?.form as QueuedIssueForm | undefined;
  const canPost = can('inv.consumption.post');

  const [header, setHeader] = useState(() => fixForm ? { ...fixForm.header } : ({
    storeId: seed?.storeId ?? intent?.storeId ?? '',
    consumedOn: initial ? initial.consumedOn.slice(0, 10) : todayIso(),
    shedId: initial?.shedId ?? '',
    flockId: initial?.flockId ?? '',
    issuedTo: initial?.issuedTo ?? '',
    remarks: initial?.remarks ?? '',
  }));
  // A new issue opens with a screenful of blank rows, so several items are
  // typed straight down the grid. Editing is never padded: a consumption
  // document holds exactly one line, which is why "+ Add item" is hidden
  // there — further rows would only be typing that the save throws away.
  const [lines, setLines] = useState<DrawLine[]>(() => {
    if (fixForm) {
      const { key: _old, ...line } = fixForm.line;
      return [emptyDrawLine(line)];
    }
    const seeded = seed
      ? [emptyDrawLine({ skuId: seed.skuId, skuLabel: `${seed.skuCode} — ${seed.skuName}`, qty: initial ? seed.qty : '', batchId: seed.batchId ?? '' })]
      : [emptyDrawLine({ skuId: intent?.skuId ?? '', batchId: intent?.batchId ?? '' })];
    return editing ? seeded : padRows(seeded, emptyDrawLine);
  });
  const [problem, setProblem] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [checking, setChecking] = useState(false);
  // What the review showed, lot by lot, and therefore exactly what is saved.
  const [plan, setPlan] = useState<Array<{ line: DrawLine; portions: IssuePortion[] }>>([]);
  const queryClient = useQueryClient();

  // Typing survives a reload, a crash or a closed window. Editing one document
  // must not offer another one's typing, so the draft is named after it.
  const draft = useDraft(
    editing ? `consumption.edit.${initial.id}` : fixing ? `consumption.fix.${fixing.id}` : 'consumption.new',
    { header, lines },
    (saved) => {
      // Drafts saved before the flock field existed have no flockId.
      setHeader({ ...saved.header, flockId: saved.header.flockId ?? '' });
      // Row keys come from a counter that restarts with the page, so restored
      // rows take fresh ones rather than colliding with a later form's.
      const restored = saved.lines.map(({ key: _old, ...rest }) => emptyDrawLine(rest));
      setLines(editing ? restored : padRows(restored, emptyDrawLine));
    },
  );

  function startAgain() {
    setHeader((h) => ({ ...h, issuedTo: '', remarks: '' }));
    setLines(padRows([emptyDrawLine()], emptyDrawLine));
    setProblem(null);
  }

  // A repeated, edited or restored line names its item but the catalog has
  // not seen it yet; until it has, the line cannot tell it is lot-tracked
  // and would offer no lot at all.
  const { loadByIds } = catalog;
  const unknownIds = [...new Set(lines.map((l) => l.skuId).filter((id) => id && !catalog.known.has(id)))].sort().join(',');
  useEffect(() => {
    if (unknownIds) void loadByIds(unknownIds.split(','));
  }, [unknownIds, loadByIds]);

  const { options: storeOptions } = useFarmScopedStore(stores, header.storeId, changeStore);

  const { data: allSheds } = useQuery({ queryKey: ['sheds'], queryFn: () => api.get('/api/v1/sheds') });

  // The flock follows the store: a farm store issues to its own farm's
  // flocks, a main store (no farm) to any of them. The shed is no longer
  // picked here; an older issue that names one keeps it while the flock
  // still agrees with it.
  const storeFarmId = storeOptions.find((s) => s.id === header.storeId)?.farmId ?? null;
  const { data: liveFlocks } = useLiveFlocks(storeFarmId ? [storeFarmId] : []);
  const flocks = header.storeId ? (liveFlocks?.items ?? []) : [];

  function changeStore(storeId: string) {
    setHeader((h) => {
      if (h.storeId === storeId) return h;
      const farmId = storeOptions.find((s) => s.id === storeId)?.farmId ?? null;
      const keep = (id: string, farmOf: string | undefined) => (id && farmId !== null && farmOf !== farmId ? '' : id);
      return {
        ...h,
        storeId,
        shedId: keep(h.shedId, allSheds?.find((s) => s.id === h.shedId)?.farmId),
        flockId: keep(h.flockId, liveFlocks?.items.find((f) => f.id === h.flockId)?.farmId),
      };
    });
  }

  // Picking a different flock drops a shed that does not hold it. The server
  // rejects a shed and flock that disagree, so the two are never left out of step.
  function changeFlock(flockId: string) {
    setHeader((h) => {
      const shed = allSheds?.find((s) => s.id === h.shedId);
      return { ...h, flockId, shedId: flockId && shed?.flockId !== flockId ? '' : h.shedId };
    });
  }

  const startedLines = lines.filter((l) => l.skuId || l.qty);

  function validate(): string | null {
    if (!header.storeId) return 'Choose the store this is issued from.';
    if (startedLines.length === 0) return 'Add at least one item.';
    if (startedLines.some((l) => !l.skuId)) return 'Every line with a quantity needs an item.';
    if (startedLines.some((l) => !(Number(l.qty) > 0))) return 'Every item needs a quantity above zero.';
    return null;
  }

  /**
   * Works out, against the lots the store holds right now, which lot each
   * line comes out of — so the review shows the lot that will actually be
   * recorded, and a lot that cannot cover its quantity is caught here rather
   * than by a failed post.
   */
  async function planIssue(): Promise<Array<{ line: DrawLine; portions: IssuePortion[] }>> {
    // Work being put right is not counted against itself.
    const waiting = (await savedWork()).filter((op) => !fixing || op.id !== fixing.id);
    return Promise.all(
      startedLines.map(async (line) => {
        const sku = catalog.known.get(line.skuId);
        const code = sku?.code ?? line.skuLabel;
        if (!sku?.isBatchTracked) {
          return { line, portions: [{ batchId: '', batchNo: null, expiryDate: null, qty: line.qty, auto: false }] };
        }
        // Shares DrawLines' cache key, so the lot dropdown refreshes too. With
        // no line, the lots this PC last saw for the item.
        const lotsKey = ['inv', 'batches', header.storeId, line.skuId];
        const read = (await fetchOrCached(
          lotsKey,
          () => api.get('/api/v1/inventory/batches', { storeId: header.storeId, skuId: line.skuId }),
          `Which lots of ${code} this store holds`,
          queryClient,
        ).catch((e: unknown) => {
          // A store whose whole holding was read here holds none it did not list.
          if (e instanceof NotOnThisPcError && queryClient.getQueryData(holdingReadKey(header.storeId)) !== undefined) return [];
          throw e;
        })) as unknown as LotOption[];
        // Less what is already waiting to be sent from each lot, so two issues
        // typed offline do not both plan on the same bags. The server checks
        // again when the work arrives.
        const readAt = queryClient.getQueryState(lotsKey)?.dataUpdatedAt ?? 0;
        const lots = read
          .map((lot) => ({ ...lot, qty: String(Number(lot.qty) - queuedOut(waiting, header.storeId, line.skuId, lot.id, readAt)) }))
          .filter((lot) => Number(lot.qty) > 0);
        const held = (q: string) => quantity(q, sku.uomPrecision);

        if (line.batchId) {
          const lot = lots.find((l) => l.id === line.batchId);
          if (!lot) {
            throw new Error(`${code}: the chosen lot is no longer held in this store. Choose another lot, or "Any lot".`);
          }
          if (Number(line.qty) > Number(lot.qty)) {
            throw new Error(
              `${code}: lot ${lot.batchNo} holds only ${held(lot.qty)} ${sku.uom}, ${line.qty} requested. ` +
                'Lower the quantity, or choose "Any lot" to draw from several lots.',
            );
          }
          return { line, portions: [{ batchId: lot.id, batchNo: lot.batchNo, expiryDate: lot.expiryDate, qty: line.qty, auto: false }] };
        }

        if (lots.length === 0) throw new Error(`${code}: no lot of this item is held in this store.`);
        // An edited issue stays one document, so it needs one lot that
        // covers it all; a new one may be spread over several.
        if (editing) {
          const fits = lots.find((l) => Number(l.qty) >= Number(line.qty));
          if (!fits) {
            throw new Error(
              `${code}: no single lot holds ${line.qty}. Lots held: ` +
                lots.map((l) => `${l.batchNo} (${held(l.qty)})`).join(', '),
            );
          }
          return { line, portions: [{ batchId: fits.id, batchNo: fits.batchNo, expiryDate: fits.expiryDate, qty: line.qty, auto: true }] };
        }
        return { line, portions: splitAcrossLots(lots, line.qty, code) };
      }),
    );
  }

  async function openReview() {
    setMessage(null);
    const issue = validate();
    setProblem(issue);
    if (issue) return;
    setChecking(true);
    try {
      setPlan(await planIssue());
      setReviewing(true);
    } catch (e) {
      setProblem(errorMessage(e, 'Could not check the lots held'));
    } finally {
      setChecking(false);
    }
  }

  async function submit(andPost: boolean) {
    setMessage(null);
    const issue = validate();
    setProblem(issue);
    if (issue) return;

    if (editing) {
      const { line, portions } = plan[0]!;
      const batchId = portions[0]!.batchId;
      // Through the outbox, carrying the version that was seen; the post, if
      // asked for, carries the version the change makes (.docs/023).
      const batch: NewOperation[] = [
        {
          kind: 'consumption.edit',
          method: 'PATCH',
          path: `${CONSUMPTION}/${initial.id}`,
          body: {
            version: initial.version,
            storeId: header.storeId,
            skuId: line.skuId,
            qty: line.qty,
            consumedOn: header.consumedOn,
            // Sent even when blank, so switching back to "Any lot" sticks.
            batchId,
            // Sent even when blank, so clearing the shed or flock sticks.
            shedId: header.shedId,
            flockId: header.flockId,
            ...(header.issuedTo.trim() ? { issuedTo: header.issuedTo.trim() } : {}),
            ...(header.remarks.trim() ? { remarks: header.remarks.trim() } : {}),
          },
          describes: `Changing ${initial.documentNo}`,
          screen: 'inv-consume',
        },
      ];
      if (andPost) {
        batch.push({
          kind: 'consumption.post',
          method: 'POST',
          path: `${CONSUMPTION}/${initial.id}/post`,
          body: { version: initial.version + 1 },
          describes: `Posting ${initial.documentNo}`,
          screen: 'inv-consume',
        });
      }
      setSaving(true);
      const saved = await saveWork(batch);
      setSaving(false);
      if (saved.outcome === 'sent') {
        invalidate();
        onSaved(andPost ? `${initial.documentNo} issued and posted.` : `${initial.documentNo} saved.`);
      } else if (saved.outcome === 'waiting') {
        onSaved(`${initial.documentNo}: ${LATER}`);
        void announceSavedOffline({ what: `The change to ${initial.documentNo}`, closeLabel: 'OK' });
      } else {
        for (const op of saved.ops) if (op.state !== 'sent') await takeBack(op.id).catch(() => undefined);
        setProblem(saved.ops.find((op) => op.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE);
      }
      return;
    }

    if (fixing) {
      const { line, portions } = plan[0]!;
      if (!line.batchId && portions.length > 1) {
        setProblem('Choose one lot for this item, or lower the quantity so one lot covers it.');
        return;
      }
      const body = {
        storeId: header.storeId,
        skuId: line.skuId,
        qty: line.qty,
        consumedOn: header.consumedOn,
        ...(portions[0]?.batchId ? { batchId: portions[0].batchId } : {}),
        ...(header.shedId ? { shedId: header.shedId } : {}),
        ...(header.flockId ? { flockId: header.flockId } : {}),
        ...(header.issuedTo.trim() ? { issuedTo: header.issuedTo.trim() } : {}),
        ...(header.remarks.trim() ? { remarks: header.remarks.trim() } : {}),
      };
      setSaving(true);
      // Sent again under a new key, with whatever stood on it following.
      const newId = await retryWork(fixing.id, {
        body,
        form: { header, line } satisfies QueuedIssueForm,
        describes: `Stock OUT ${shortDate(header.consumedOn)}: ${line.qty} of ${line.skuLabel}`,
      });
      const saved = newId ? await awaitWork([newId]) : null;
      setSaving(false);
      if (!saved || saved.outcome === 'refused' || saved.outcome === 'not-kept') {
        setProblem(saved?.ops[0]?.lastError ?? 'The server still refused it. It is kept under Offline work, Needs attention.');
        return;
      }
      draft.forget();
      onSaved(saved.outcome === 'sent' ? 'Put right and sent.' : LATER);
      if (saved.outcome === 'waiting') void announceSavedOffline({ what: fixing.describes, closeLabel: 'OK' });
      return;
    }

    setSaving(true);
    let answered = 0;
    let kept = 0;
    const failed: string[] = [];
    const completed = new Set<string>();
    const leftover = new Map<string, string>();
    // Saved exactly as reviewed: a lot-tracked line with no lot chosen comes
    // out of the oldest lots first (FIFO), one entry per lot it draws on. The
    // server re-checks every balance, so stock moved since is still caught.
    //
    // Each item goes through the outbox on its own (.docs/023): with a line it
    // is answered at once, as before; with none it is kept on this PC and sent
    // later. Either way it leaves the form, because it is safe.
    for (const { line, portions } of plan) {
      const code = catalog.known.get(line.skuId)?.code ?? line.skuLabel;
      const batch: NewOperation[] = [];
      for (const portion of portions) {
        const at = batch.length;
        batch.push({
          kind: 'consumption.create',
          method: 'POST',
          path: CONSUMPTION,
          body: {
            storeId: header.storeId,
            skuId: line.skuId,
            qty: portion.qty,
            consumedOn: header.consumedOn,
            ...(portion.batchId ? { batchId: portion.batchId } : {}),
            ...(header.shedId ? { shedId: header.shedId } : {}),
            ...(header.flockId ? { flockId: header.flockId } : {}),
            ...(header.issuedTo.trim() ? { issuedTo: header.issuedTo.trim() } : {}),
            ...(header.remarks.trim() ? { remarks: header.remarks.trim() } : {}),
          },
          describes: `Stock OUT ${shortDate(header.consumedOn)}: ${portion.qty} of ${line.skuLabel || code}`,
          screen: 'inv-consume',
          form: { header, line: { ...line, qty: portion.qty, batchId: portion.batchId } } satisfies QueuedIssueForm,
        });
        if (andPost) {
          batch.push({
            kind: 'consumption.post',
            method: 'POST',
            path: `${CONSUMPTION}/{dep}/post`,
            after: at,
            describes: `Posting the Stock OUT of ${code}`,
            screen: 'inv-consume',
          });
        }
      }

      const saved = await saveWork(batch);
      if (saved.outcome === 'sent' || saved.outcome === 'waiting') {
        if (saved.outcome === 'sent') answered += 1;
        else kept += 1;
        completed.add(line.key);
        continue;
      }

      // Refused while this form is open: said here, on the form, rather than
      // left in the queue for Needs attention as well. What the server did take
      // stays; whatever had not gone yet is taken back with the refusal.
      // `not-kept`: no copy here to keep it in, and no answer: nothing is saved.
      const stuck = saved.ops.find((op) => op.state === 'refused') ?? saved.ops.find((op) => op.state !== 'sent');
      const refusal = { kind: stuck?.kind ?? 'consumption.create', lastError: stuck?.lastError ?? UNREACHABLE_MESSAGE };
      for (const op of saved.ops) if (op.state !== 'sent') await takeBack(op.id).catch(() => undefined);
      const draftLeft = saved.ops.find(
        (op) => op.kind === 'consumption.create' && op.state === 'sent' && andPost && refusal.kind === 'consumption.post',
      );
      const madeDoc = draftLeft?.result as { documentNo?: string } | undefined;
      failed.push(
        madeDoc?.documentNo
          ? `${code} (saved as draft ${madeDoc.documentNo}, not posted: ${refusal.lastError ?? 'refused'})`
          : `${code} (${refusal.lastError ?? 'refused'})`,
      );
      if (refusal.kind === 'consumption.post') {
        // The issue itself was made: only its post is left, from the list.
        completed.add(line.key);
        continue;
      }
      const gone = saved.ops
        .filter((op) => op.kind === 'consumption.create' && op.state === 'sent')
        .map((op) => (op.body as { qty: string }).qty);
      if (gone.length > 0) leftover.set(line.key, quantityLeft(line.qty, gone));
    }
    setSaving(false);
    invalidate();

    const keptWords =
      kept === 0
        ? ''
        : useConnection.getState().online
          ? `${kept} item(s) saved on this PC and being sent.`
          : `${kept} item(s) saved on this PC. They will be sent when the connection is back.`;

    if (failed.length === 0) {
      const answeredWords =
        answered === 0 ? '' : andPost ? `${answered} item(s) issued and posted.` : `${answered} item(s) saved as drafts.`;
      draft.forget();
      const said = [answeredWords, keptWords].filter(Boolean).join(' ');
      setMessage(said);
      setLines(padRows([emptyDrawLine()], emptyDrawLine));
      setHeader((h) => ({ ...h, issuedTo: '', remarks: '' }));
      if (kept > 0) {
        void announceSavedOffline({
          what: `Stock OUT ${shortDate(header.consumedOn)}: ${kept} ${kept === 1 ? 'item' : 'items'}`,
          canEnterAnother: true,
          note: 'It gets its document number when it is sent.',
        }).then((choice) => {
          if (choice !== 'another') onSaved(said);
        });
      }
      return;
    }

    // What went through, or is safe on this PC, leaves the form, so saving
    // again cannot send it again.
    setLines((current) =>
      padRows(
        current
          .filter((l) => !completed.has(l.key))
          .map((l) => {
            const left = leftover.get(l.key);
            return left === undefined ? l : { ...l, qty: left };
          }),
        emptyDrawLine,
      ),
    );
    setProblem(
      [
        `${answered + kept} of ${plan.length} issued. Failed: ${failed.join(', ')}.`,
        completed.size > 0 ? 'The items that went through are off the form. Only what failed is left.' : '',
        keptWords,
      ]
        .filter(Boolean)
        .join(' '),
    );
  }

  const storeName = storeOptions.find((s) => s.id === header.storeId);
  const flockName = flocks.find((f) => f.id === header.flockId);

  const panel = (
    <ComposePanel
      icon="outbound"
      splitId="compose-consumption"
      title={editing ? `Edit ${initial.documentNo}` : fixing ? 'Put right a refused Stock OUT' : 'Issue stock'}
      intro="Records what left the store and where it went. The cost is taken from the running average at the moment it is posted."
      onCancel={() => {
        // Only reached once the discard was confirmed, so the draft goes too.
        draft.forget();
        onCancel();
      }}
      cancelLabel={editing ? 'Cancel' : 'Back to list'}
      top={
        <div className="stack">
          <DraftNotice draft={draft} onStartAgain={startAgain} />
          <FormGroup title="From, when and where" template="2.2fr 1.3fr 1fr 1.2fr">
            <StorePicker
              label="From store"
              required
              value={header.storeId}
              onChange={changeStore}
              stores={stores && storeOptions}
            />
            <Select
              label="Flock"
              value={header.flockId}
              onChange={changeFlock}
              options={flocks.map((f) => ({ value: f.id, label: storeFarmId ? `${f.code} · ${f.nameEn}` : `${f.farmCode} · ${f.code} · ${f.nameEn}` }))}
              placeholder={header.storeId ? 'No flock' : undefined}
              prompt="Choose a store first"
              disabled={!header.storeId}
              hint={header.storeId && flocks.length === 0 ? 'No live flock on this store’s farm.' : undefined}
            />
            <Field label="Date" type="date" required value={header.consumedOn} onChange={(consumedOn) => setHeader((h) => ({ ...h, consumedOn }))} />
            <Field label="Issued to" value={header.issuedTo} onChange={(issuedTo) => setHeader((h) => ({ ...h, issuedTo }))} hint="Whoever collected it" />
            <Field label="Remarks" value={header.remarks} onChange={(remarks) => setHeader((h) => ({ ...h, remarks }))} span="full" />
          </FormGroup>
        </div>
      }
      bottom={
        <>
          <FormGroup title="Items" hint={header.storeId ? 'Search by item code or name.' : 'Choose a store first.'}>
            <DrawLines
              lines={lines}
              onChange={setLines}
              skus={catalog.known}
              search={catalog.search}
              storeId={header.storeId}
              allowAdd={!editing}
              lotOptional
              minRows={editing ? 1 : ENTRY_ROWS}
            />
          </FormGroup>

        </>
      }
      footer={
        <>
          {problem && <ErrorNote message={problem} />}
          {message && <OkNote message={message} />}

          <FormActions onCancel={onCancel} cancelLabel={editing ? 'Cancel' : 'Close'}>
            <button type="button" className="btn btn--primary" disabled={saving || checking} onClick={() => void openReview()}>
              {checking ? 'Checking lots…' : 'Review & Save'}
            </button>
          </FormActions>
        </>
      }
    />
  );

  return (
    <>
      {panel}
      {reviewing && (
        <ReviewSummaryDialog
          icon="outbound"
          title={editing ? `Review ${initial.documentNo}` : 'Review before issuing'}
          facts={[
            ['From store', storeName ? `${storeName.code} — ${storeName.nameEn}` : '—'],
            ['Date', header.consumedOn],
            ['Flock', flockName ? `${flockName.code} · ${flockName.nameEn}` : 'None'],
            ['Issued to', header.issuedTo.trim() || '—'],
            ['Remarks', header.remarks.trim() || '—'],
          ]}
          lines={plan.flatMap(({ line, portions }) => {
            const sku = catalog.known.get(line.skuId);
            return portions.map((p) => ({
              item: line.skuLabel || sku?.code || line.skuId,
              qty: sku ? `${quantity(p.qty, sku.uomPrecision)}${packedSuffix(p.qty, sku)}` : p.qty,
              lot: lotLabel(p),
            }));
          })}
          note={
            plan.some(({ portions }) => portions.some((p) => p.auto))
              ? editing
                ? 'Lot marked (auto) is the oldest one that covers the quantity.'
                : 'Lots marked (auto) were picked oldest first. An item drawn from more than one lot is saved as one issue per lot.'
              : undefined
          }
          onClose={() => setReviewing(false)}
          primary={{
            label: canPost ? (editing ? 'Save and post' : 'Issue and post') : editing ? 'Save changes' : 'Save draft',
            pending: saving,
            onClick: () => {
              setReviewing(false);
              void submit(canPost);
            },
          }}
          secondary={
            canPost
              ? {
                  label: editing ? 'Save changes' : 'Save draft',
                  pending: saving,
                  onClick: () => {
                    setReviewing(false);
                    void submit(false);
                  },
                }
              : undefined
          }
        />
      )}
    </>
  );
}
